// YouTube mode navigator (clone of ../views/Navigator.tsx).
//
//   .nav-sidebar (260px) | .nav-main (flex)   | .nav-details (40vw)
//   list tree            | embedded player    | video details + rating
//
// Selection lives in URL search params (?list=queue&video=<id>); the video
// detail is fetched once here and shared by the player + details panes.
//
// Rate mode (?rate=1, entered via the header "rate" button) steps through the
// queue: the head of the queue is auto-selected, and after each rating/skip
// removes it, selection advances to the next item.

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { loadLayout } from "../api/layout";
import { useYtListItems, useYtVideo, type YtListName } from "./api";
import { PlayerPane } from "./PlayerPane";
import {
  cycleYtField,
  type YtFilterState,
  loadYtSort,
  saveYtSort,
  type YtSortState,
  ytViewItems,
} from "./sort";
import { YoutubeDetails, type YtRateModeState } from "./YoutubeDetails";
import { YoutubeListTree } from "./YoutubeListTree";

import "../views/Navigator.css";

export function YoutubeNavigator() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("video");
  const selectedList = (params.get("list") as YtListName | null) ?? null;
  const rateMode = params.get("rate") === "1";
  const { data: video, error, isLoading } = useYtVideo(selectedId);
  const { data: queue } = useYtListItems(rateMode ? "queue" : null);

  // Panel-side preference (shared with the cinematic app; read once on mount).
  const swapped = useMemo(() => loadLayout() === "swapped", []);

  // Sort + filter apply to every list — owned here so the tree display,
  // arrow-key nav, and advance-in-source all agree on the order.
  const [sort, setSort] = useState<YtSortState>(loadYtSort);
  const [filter, setFilter] = useState<YtFilterState | null>(null);
  function updateSort(next: YtSortState) {
    setSort(next);
    saveYtSort(next);
  }
  function cycleSortField() {
    const field = cycleYtField(sort.field);
    updateSort({ ...sort, field });
    setFilter(null);
  }
  function setFilterValue(value: string | null) {
    setFilter(value == null ? null : { field: sort.field, value });
  }

  // Arrow-key navigation: ↑/↓ cycle through the currently selected list.
  const { data: currentList } = useYtListItems(selectedList);
  const sortedSiblings = useMemo(
    () => ytViewItems(currentList?.items ?? [], sort, filter),
    [currentList, sort, filter],
  );
  useEffect(() => {
    const items = sortedSiblings;
    if (!selectedList || items.length === 0) return;

    function onKey(e: KeyboardEvent) {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" ||
          t.tagName === "TEXTAREA" ||
          t.tagName === "SELECT" ||
          t.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      const idx = selectedId ? items.findIndex((i) => i.video_id === selectedId) : -1;
      const nextIdx =
        e.key === "ArrowDown"
          ? idx < 0
            ? 0
            : (idx + 1) % items.length
          : idx < 0
            ? items.length - 1
            : (idx - 1 + items.length) % items.length;
      const next = new URLSearchParams(params);
      next.set("list", selectedList!);
      next.set("video", items[nextIdx].video_id);
      setParams(next, { replace: true });
    }

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sortedSiblings, selectedId, selectedList, params, setParams]);

  const queueItems = queue?.items ?? [];
  const queueIndex = selectedId ? queueItems.findIndex((i) => i.video_id === selectedId) : -1;

  // Rate mode: keep a queue item selected. When the current selection leaves
  // the queue (rated or skipped away) — or nothing is selected yet — advance
  // to the queue head.
  const prevQueueLen = useRef<number | null>(null);
  useEffect(() => {
    if (!rateMode || !queue) return;
    const len = queueItems.length;
    if (len === 0) {
      // Clear the selection exactly ONCE, at the moment the queue empties, so
      // the "queue is empty" screen (with its fill button) shows.
      if (prevQueueLen.current !== null && prevQueueLen.current > 0 && selectedId != null) {
        const next = new URLSearchParams(params);
        next.delete("video");
        setParams(next, { replace: true });
      }
      prevQueueLen.current = 0;
      return;
    }
    prevQueueLen.current = len;
    if (queueIndex === -1) {
      const next = new URLSearchParams(params);
      next.set("list", "queue");
      next.set("video", queueItems[0].video_id);
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rateMode, queue, selectedId]);

  function exitRateMode() {
    const next = new URLSearchParams(params);
    next.delete("rate");
    setParams(next, { replace: true });
  }

  const rateState: YtRateModeState | null = rateMode
    ? {
        position: queueIndex >= 0 ? queueIndex + 1 : 0,
        total: queueItems.length,
        onExit: exitRateMode,
      }
    : null;

  return (
    <div className={"nav-shell" + (swapped ? " nav-shell--swapped" : "")}>
      <aside className="nav-sidebar">
        <div className="nav-sidebar__inner">
          <YoutubeListTree
            sort={sort}
            filter={filter}
            onCycleField={cycleSortField}
            onToggleDir={() => updateSort({ ...sort, dir: sort.dir === "asc" ? "desc" : "asc" })}
            onSetFilter={setFilterValue}
          />
        </div>
      </aside>

      <section className="nav-main">
        <PlayerPane videoId={selectedId} video={video} />
      </section>

      <aside className="nav-details">
        <YoutubeDetails
          videoId={selectedId}
          video={video}
          error={error}
          isLoading={isLoading}
          rateMode={rateState}
          siblings={sortedSiblings}
        />
      </aside>
    </div>
  );
}
